import { getRepository } from '@things-factory/shell'
import type ResolverContext from '@things-factory/auth-base'
import { KpiStatisticCalculationService } from './kpi-statistic-calculation.service'
import { KpiPeriodType } from '../kpi/kpi'
import { Domain } from '@things-factory/shell'

/**
 * KPI 통계 배치 계산 서비스
 * 정기적으로 실행하여 KpiValue 데이터를 집계해서 KpiStatistic을 생성/업데이트
 */
export class KpiStatisticBatchService {
  
  /**
   * 일일 배치: 어제 날짜의 모든 통계 계산
   */
  static async runDailyBatch(domainId?: string): Promise<void> {
    console.log('[KPI Statistics] Starting daily batch calculation...')
    
    const yesterday = new Date()
    yesterday.setDate(yesterday.getDate() - 1)
    const valueDate = yesterday.toISOString().split('T')[0] // YYYY-MM-DD
    
    await this.calculateForAllDomains(KpiPeriodType.DAY, valueDate, domainId)
    
    console.log(`[KPI Statistics] Daily batch completed for ${valueDate}`)
  }
  
  /**
   * 월간 배치: 지난 달의 모든 통계 계산
   */
  static async runMonthlyBatch(domainId?: string): Promise<void> {
    console.log('[KPI Statistics] Starting monthly batch calculation...')
    
    const lastMonth = new Date()
    lastMonth.setMonth(lastMonth.getMonth() - 1)
    const valueDate = lastMonth.toISOString().substring(0, 7) // YYYY-MM
    
    await this.calculateForAllDomains(KpiPeriodType.MONTH, valueDate, domainId)
    
    console.log(`[KPI Statistics] Monthly batch completed for ${valueDate}`)
  }
  
  /**
   * 분기 배치: 지난 분기의 모든 통계 계산
   */
  static async runQuarterlyBatch(domainId?: string): Promise<void> {
    console.log('[KPI Statistics] Starting quarterly batch calculation...')
    
    const now = new Date()
    const currentQuarter = Math.floor(now.getMonth() / 3) + 1
    const lastQuarter = currentQuarter === 1 ? 4 : currentQuarter - 1
    const year = currentQuarter === 1 ? now.getFullYear() - 1 : now.getFullYear()
    const valueDate = `${year}-Q${lastQuarter}`
    
    await this.calculateForAllDomains(KpiPeriodType.QUARTER, valueDate, domainId)
    
    console.log(`[KPI Statistics] Quarterly batch completed for ${valueDate}`)
  }
  
  /**
   * 연간 배치: 작년의 모든 통계 계산
   */
  static async runYearlyBatch(domainId?: string): Promise<void> {
    console.log('[KPI Statistics] Starting yearly batch calculation...')
    
    const lastYear = new Date().getFullYear() - 1
    const valueDate = lastYear.toString()
    
    await this.calculateForAllDomains(KpiPeriodType.YEAR, valueDate, domainId)
    
    console.log(`[KPI Statistics] Yearly batch completed for ${valueDate}`)
  }
  
  /**
   * 대시보드용 지역별 통계 계산
   */
  static async runDashboardBatch(
    periodType: KpiPeriodType = KpiPeriodType.MONTH,
    valueDate?: string,
    domainId?: string
  ): Promise<void> {
    console.log('[KPI Statistics] Starting dashboard batch calculation...')
    
    if (!valueDate) {
      const now = new Date()
      if (periodType === KpiPeriodType.MONTH) {
        now.setMonth(now.getMonth() - 1)
        valueDate = now.toISOString().substring(0, 7) // YYYY-MM
      } else {
        now.setDate(now.getDate() - 1)
        valueDate = now.toISOString().split('T')[0] // YYYY-MM-DD
      }
    }
    
    await this.calculateForAllDomains(periodType, valueDate, domainId)
    
    console.log(`[KPI Statistics] Dashboard batch completed for ${valueDate} (${periodType})`)
  }
  
  /**
   * 모든 도메인에 대해 통계 계산
   */
  private static async calculateForAllDomains(
    periodType: KpiPeriodType,
    valueDate: string,
    domainId?: string
  ): Promise<void> {
    try {
      const domains = domainId 
        ? await getRepository(Domain).find({ where: { id: domainId } })
        : await getRepository(Domain).find()
      
      for (const domain of domains) {
        try {
          await this.calculateForDomain(domain, periodType, valueDate)
        } catch (error) {
          console.error(`[KPI Statistics] Domain ${domain.name} calculation failed, continuing with next domain:`, error)
          // 개별 도메인 실패가 전체 배치를 중단시키지 않도록 계속 진행
          continue
        }
      }
    } catch (error) {
      console.error('[KPI Statistics] Batch calculation failed:', error)
      throw error
    }
  }
  
  /**
   * 특정 도메인에 대해 통계 계산
   */
  private static async calculateForDomain(
    domain: Domain,
    periodType: KpiPeriodType,
    valueDate: string
  ): Promise<void> {
    console.log(`[KPI Statistics] Calculating for domain: ${domain.name} (${domain.id})`)
    
    // 가짜 ResolverContext 생성 (배치 작업용)
    const context: ResolverContext = {
      state: {
        domain,
        user: { id: 'system', name: 'System Batch' }, // 시스템 사용자
        tx: null // 트랜잭션 없음 (각 계산마다 개별 트랜잭션)
      }
    } as any
    
    try {
      const statistics = await KpiStatisticCalculationService.calculateAllStatistics(
        periodType,
        valueDate,
        context
      )
      
      console.log(`[KPI Statistics] Generated ${statistics.length} statistics for domain ${domain.name}`)
      
      // 성공 로깅
      this.logBatchResult(domain.id, periodType, valueDate, statistics.length, 'SUCCESS')
      
    } catch (error) {
      console.error(`[KPI Statistics] Failed to calculate for domain ${domain.name}:`, error)
      
      // 실패 로깅
      this.logBatchResult(domain.id, periodType, valueDate, 0, 'FAILED', error.message)
      
      // 에러를 던져서 상위에서 처리
      throw error
    }
  }
  
  /**
   * 배치 실행 결과 로깅
   */
  private static logBatchResult(
    domainId: string,
    periodType: KpiPeriodType,
    valueDate: string,
    statisticsCount: number,
    status: 'SUCCESS' | 'FAILED',
    errorMessage?: string
  ): void {
    const logData = {
      timestamp: new Date().toISOString(),
      domainId,
      periodType,
      valueDate,
      statisticsCount,
      status,
      errorMessage
    }
    
    // 실제 운영에서는 로깅 시스템에 저장
    console.log('[KPI Statistics Batch Log]', JSON.stringify(logData))
  }
  
  /**
   * 배치 작업 스케줄링 설정 예시
   */
  static setupBatchSchedule(): void {
    // 실제 구현시에는 cron 등의 스케줄러 사용
    console.log('[KPI Statistics] Batch schedule setup example:')
    console.log('Daily batch: Run at 02:00 AM every day')
    console.log('Monthly batch: Run at 03:00 AM on 1st of every month') 
    console.log('Quarterly batch: Run at 04:00 AM on 1st of every quarter')
    console.log('Yearly batch: Run at 05:00 AM on January 1st')
    
    /*
    // Node-cron 사용 예시:
    import cron from 'node-cron'
    
    // 매일 새벽 2시
    cron.schedule('0 2 * * *', () => {
      this.runDailyBatch()
    })
    
    // 매월 1일 새벽 3시
    cron.schedule('0 3 1 * *', () => {
      this.runMonthlyBatch()
    })
    
    // 매 분기 첫날 새벽 4시 (1, 4, 7, 10월)
    cron.schedule('0 4 1 1,4,7,10 *', () => {
      this.runQuarterlyBatch()
    })
    
    // 매년 1월 1일 새벽 5시
    cron.schedule('0 5 1 1 *', () => {
      this.runYearlyBatch()
    })
    */
  }
}